import { useMutation } from '@apollo/client/react';
import type { FC, ReactNode } from 'react';
import { useNavigate } from 'react-router';

import { Form, FormButtons, FormInput, FormPhoneNumber } from '@zen/Components';
import type { FormInstance } from '@zen/Components/types';
import { Headline } from '@zen/DesignSystem';
import { dashboardRoutes } from '@zen/Routes';
import useAccount from '@zen/utils/hooks/useAccount';
import { useNotification } from '@zen/utils/hooks/useNotification';
import type { IOkOrErrorResult } from '@zen/utils/OkOrErrorResult';
import { performFormMutation } from '@zen/utils/performMutation';

import FormTermsAndConditionsCheckbox from '../components/forms/FormTermsAndConditionsCheckbox/FormTermsAndConditionsCheckbox';
import { validationSchema } from './captureProfileUsersName.validation';
import {
  UpdateAccountMemberProfileInformationDocument,
  type UpdateAccountMemberProfileInformationMutationResult,
  type UpdateAccountMemberProfileInformationMutationVariables
} from './graphql';
import Logo from './logo.svg?react';

interface UsersName {
  name: string;
  phoneNumber: string;
  terms: boolean;
}

interface Props {
  onSuccess?: () => void;
}

const CaptureProfileUsersName: FC<Props> = ({ onSuccess }) => {
  const { userProfile } = useAccount();
  const initialValues: UsersName = {
    name: userProfile.name,
    phoneNumber: userProfile.phoneNumber,
    terms: false
  };
  const { addError } = useNotification();
  const [updateAccountMember] = useMutation<
    UpdateAccountMemberProfileInformationMutationResult,
    UpdateAccountMemberProfileInformationMutationVariables
  >(UpdateAccountMemberProfileInformationDocument);
  const navigate = useNavigate();

  const renderFormButtons = ({ isSubmitting }: FormInstance<UsersName>): ReactNode => {
    return <FormButtons className="flex-1" isSubmitting={isSubmitting} text="Complete profile" />;
  };

  const handleUpdateAccountMemberProfile = ({ name, phoneNumber }: UsersName): Promise<IOkOrErrorResult> => {
    return performFormMutation({
      mutationFn: () =>
        updateAccountMember({
          variables: {
            input: {
              name,
              phoneNumber
            }
          }
        }),
      onError: () => addError()
    });
  };

  const handleSuccess = (): void | Promise<void> => {
    return onSuccess ? onSuccess() : navigate(dashboardRoutes.dashboard.getUrl());
  };

  return (
    <div
      className="fixed inset-0 flex flex-col items-center justify-center p-20 bg-white"
      data-testid="capture-profile-users-name-page"
    >
      <div className="flex flex-col max-w-xs">
        <Logo className="w-32 mb-16" data-testid="logo" />
        <Headline level={1}>Complete your profile</Headline>
        <p className="mt-2 mb-8 text-grey-base">Please enter your name and phone number.</p>
        <Form
          formButtons={renderFormButtons}
          initialValues={initialValues}
          onSubmit={handleUpdateAccountMemberProfile}
          onSuccess={handleSuccess}
          validationSchema={validationSchema}
        >
          {() => {
            return (
              <div className="flex flex-col text-left">
                <FormInput name="name" />
                <FormPhoneNumber name="phoneNumber" />
                <FormTermsAndConditionsCheckbox className="pr-9" name="terms" />
              </div>
            );
          }}
        </Form>
      </div>
    </div>
  );
};

export default CaptureProfileUsersName;
